// Client-side export helpers for the Access-codes keys table (9b).
// The repo has no generic CSV utility (existing CSV/PDF exports are server blob
// downloads), so Export CSV + Print sheet are built from the in-memory rows here.

export type AccessKeyStatus = 'used' | 'expired' | 'unused';

export interface AccessKeyRow {
  id: string;
  code: string;
  usedAt?: string | null;
  expiresAt?: string | null;
  candidate?: { id: string; name?: string; email?: string } | null;
  createdAt?: string;
}

export const accessKeyStatus = (row: AccessKeyRow): AccessKeyStatus => {
  if (row.usedAt) return 'used';
  if (row.expiresAt && new Date(row.expiresAt) < new Date()) return 'expired';
  return 'unused';
};

const usedByLabel = (row: AccessKeyRow): string =>
  row.candidate?.name || row.candidate?.email || '';

const formatDateTime = (value?: string | null): string =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '';

// RFC-4180 field escaping: quote when the value contains a comma, quote, or newline.
const csvCell = (value: string): string =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

export interface AccessKeyColumnLabels {
  code: string;
  status: string;
  usedBy: string;
  usedAt: string;
  expires: string;
  statusText: Record<AccessKeyStatus, string>;
}

const rowCells = (row: AccessKeyRow, labels: AccessKeyColumnLabels): string[] => [
  row.code,
  labels.statusText[accessKeyStatus(row)],
  usedByLabel(row),
  formatDateTime(row.usedAt),
  formatDateTime(row.expiresAt),
];

/** Runs entirely client-side — no backend endpoint. */
export function exportAccessKeysCsv(
  rows: AccessKeyRow[],
  labels: AccessKeyColumnLabels,
  fileName = 'access-codes.csv',
): void {
  const header = [labels.code, labels.status, labels.usedBy, labels.usedAt, labels.expires];
  const lines = [header, ...rows.map((row) => rowCells(row, labels))]
    .map((cells) => cells.map(csvCell).join(','))
    .join('\r\n');
  // Prepend a BOM so Excel opens Cyrillic/Latin-uzbek correctly.
  const blob = new Blob(['﻿' + lines], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"]/g, (ch) => {
    switch (ch) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      default:
        return '&quot;';
    }
  });

/** Mirrors WorksheetPage's print flow. Falls back silently when a popup is blocked. */
export function printAccessKeysSheet(
  rows: AccessKeyRow[],
  labels: AccessKeyColumnLabels,
  title: string,
): void {
  const win = window.open('', '_blank', 'noopener,noreferrer,width=900,height=700');
  if (!win) return;
  const head = [labels.code, labels.status, labels.usedBy, labels.usedAt, labels.expires];
  const body = rows
    .map(
      (row) =>
        `<tr>${rowCells(row, labels)
          .map((cell, i) => `<td class="${i === 0 ? 'mono' : ''}">${escapeHtml(cell || '—')}</td>`)
          .join('')}</tr>`,
    )
    .join('');
  win.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>` +
      `<style>` +
      `*{box-sizing:border-box}body{font-family:ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif;color:#111827;margin:32px}` +
      `h1{font-size:18px;font-weight:600;margin:0 0 16px}` +
      `table{width:100%;border-collapse:collapse;font-size:12px}` +
      `th,td{border:1px solid #E5E7EB;padding:6px 10px;text-align:left}` +
      `th{background:#F9FAFB;font-weight:600;text-transform:uppercase;font-size:10px;letter-spacing:.05em;color:#6B7280}` +
      `td.mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-weight:600}` +
      `@media print{body{margin:0}}` +
      `</style></head><body>` +
      `<h1>${escapeHtml(title)}</h1>` +
      `<table><thead><tr>${head.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead>` +
      `<tbody>${body}</tbody></table>` +
      `</body></html>`,
  );
  win.document.close();
  win.focus();
  win.print();
}
